import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../../redux/IchthusSlice";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import {
  RotateCcw,
  Search,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Package,
  User,
  Clock,
  RefreshCw,
  CheckCircle,
  TrendingDown,
  Layers,
  History,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import Loader from "../../../loader/Loader";
import Pagination from "../../Pagination";
import { domain } from "../../../../security";
import AddTradeReturnModal from "./AddTradeReturnModal";
import ProcessTradeReturnModal from "./ProcessTradeReturnModal";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const StatCard = ({ label, value, Icon, colorClass }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
    <div className={`p-3 rounded-lg ${colorClass}`}>
      <Icon size={22} />
    </div>
    <div>
      <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wide">
        {label}
      </p>
      <p className="text-xl font-black text-slate-800">{value}</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HISTORY MODAL
// ─────────────────────────────────────────────────────────────────────────────
const TradeReturnHistoryModal = ({ isOpen, onClose }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${domain}/api/TradeReturns/history?search=${search}`,
      );
      setHistoryData(res.data || []);
    } catch {
      toast.error("Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
      setExpandedRow(null);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const paginated = historyData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <History size={18} />
            </div>
            Deleted Trade Returns History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Return # or Customer..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-100"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <History size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No deleted trade return records.</p>
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-widest sticky top-0">
                <tr>
                  <th className="px-5 py-3.5 w-8"></th>
                  <th className="px-5 py-3.5">Return #</th>
                  <th className="px-5 py-3.5">Customer</th>
                  <th className="px-5 py-3.5 text-right">Refund Amount</th>
                  <th className="px-5 py-3.5">Created By</th>
                  <th className="px-5 py-3.5">Deleted By</th>
                  <th className="px-5 py-3.5">Deleted On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.map((ret) => (
                  <React.Fragment key={ret.id}>
                    <tr
                      onClick={() =>
                        setExpandedRow(expandedRow === ret.id ? null : ret.id)
                      }
                      className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4 text-slate-400">
                        {expandedRow === ret.id ? (
                          <ChevronDown size={15} />
                        ) : (
                          <ChevronRight size={15} />
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-black text-slate-700 font-mono text-xs">
                          {ret.returnNumber}
                        </div>
                        <span
                          className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded mt-1 inline-block border ${
                            ret.isProcessed
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : "bg-amber-100 text-amber-700 border-amber-200"
                          }`}
                        >
                          {ret.isProcessed ? "Was Processed" : "Was Pending"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                          <User size={12} className="text-slate-400 shrink-0" />{" "}
                          {ret.customerName}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right font-black text-red-500">
                        {formatCurrency(ret.totalRefundAmount)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs font-semibold text-slate-700">
                          {ret.createdByFullName || "—"}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {formatDate(ret.createdDate)}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs font-semibold text-red-600">
                          {ret.deletedByFullName || "—"}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs text-slate-600 flex items-center gap-1">
                          <Clock size={10} className="text-slate-400" />{" "}
                          {formatDate(ret.deletedDate)}
                        </div>
                      </td>
                    </tr>

                    {expandedRow === ret.id && (
                      <tr className="bg-slate-50/50">
                        <td colSpan={7} className="px-5 py-4 pl-16">
                          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm max-w-3xl">
                            <p className="text-[10px] font-black uppercase text-slate-400 mb-3 flex items-center gap-1.5">
                              <Package size={12} /> Items
                            </p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-slate-50 text-[9px] uppercase text-slate-500 font-black">
                                  <th className="p-2 pl-3 text-left">
                                    Product
                                  </th>
                                  <th className="p-2 text-center">Qty</th>
                                  <th className="p-2 text-center">Condition</th>
                                  <th className="p-2 text-right pr-3">
                                    Refund
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {(ret.items || []).map((item, idx) => (
                                  <tr key={idx}>
                                    <td className="p-2 pl-3 font-bold text-slate-700">
                                      {item.productName}
                                    </td>
                                    <td className="p-2 text-center">
                                      {item.quantityReturned}
                                    </td>
                                    <td className="p-2 text-center">
                                      <span
                                        className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                          item.condition === "Good"
                                            ? "text-emerald-700"
                                            : "text-orange-600"
                                        }`}
                                      >
                                        {item.condition}
                                      </span>
                                    </td>
                                    <td className="p-2 text-right pr-3 font-black text-red-500">
                                      {formatCurrency(item.refundAmount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {ret.reason && (
                              <div className="mt-3 pt-2 border-t border-slate-100 text-xs flex gap-2">
                                <span className="font-black uppercase text-slate-400">
                                  Reason:
                                </span>
                                <span className="text-slate-600 italic">
                                  {ret.reason}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && historyData.length > 0 && (
          <div className="shrink-0 border-t border-slate-100 px-4 py-3">
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={historyData.length}
              currentPage={currentPage}
              paginate={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function AllTradeReturns() {
  const currentUserId = useSelector(selectUserID);

  const [tradeReturns, setTradeReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${domain}/api/TradeReturns`);
      setTradeReturns(res.data || []);
    } catch {
      toast.error("Failed to fetch Trade Returns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pendingReturns = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return tradeReturns
      .filter((r) => !r.isProcessed)
      .filter(
        (r) =>
          !t ||
          (r.returnNumber || "").toLowerCase().includes(t) ||
          (r.customerName || "").toLowerCase().includes(t),
      );
  }, [tradeReturns, searchTerm]);

  const processedReturns = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return tradeReturns
      .filter((r) => r.isProcessed)
      .filter(
        (r) =>
          !t ||
          (r.returnNumber || "").toLowerCase().includes(t) ||
          (r.customerName || "").toLowerCase().includes(t),
      );
  }, [tradeReturns, searchTerm]);

  const totalRefunded = processedReturns.reduce(
    (a, b) => a + (b.totalRefundAmount || 0),
    0,
  );
  const totalReplacements = processedReturns.filter((r) =>
    r.items?.some((i) => i.replaceQty > 0),
  ).length;

  const handleDelete = async (ret) => {
    if (
      !window.confirm(
        `Delete this ${ret.isProcessed ? "processed" : "pending"} trade return?\n\nNote: No inventory reversal is performed for trade returns.`,
      )
    )
      return;
    setPendingDeleteId(ret.id);
    try {
      await axios.put(`${domain}/api/TradeReturns/soft-delete/${ret.id}`, {
        UserID: currentUserId,
      });
      toast.success("Trade Return deleted.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed.");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const openProcessModal = (ret) => {
    setSelectedReturn(ret);
    setIsProcessModalOpen(true);
  };

  const handleTabChange = (id) => {
    setActiveTab(id);
    setCurrentPage(1);
    setExpandedRow(null);
    setSearchTerm("");
  };

  const currentList =
    activeTab === "Pending" ? pendingReturns : processedReturns;
  const paginated = currentList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="min-h-screen bg-slate-100 pb-14">
      <ToastContainer autoClose={2500} position="top-right" />

      {/* Modals */}
      {isAddModalOpen && (
        <AddTradeReturnModal
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            setIsAddModalOpen(false);
            fetchData();
          }}
          currentUserId={currentUserId}
        />
      )}
      {isProcessModalOpen && selectedReturn && (
        <ProcessTradeReturnModal
          returnRecord={selectedReturn}
          onClose={() => {
            setIsProcessModalOpen(false);
            setSelectedReturn(null);
          }}
          onSuccess={() => {
            setIsProcessModalOpen(false);
            setSelectedReturn(null);
            setActiveTab("Processed");
            fetchData();
          }}
          currentUserId={currentUserId}
        />
      )}
      <TradeReturnHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <RotateCcw className="text-emerald-600" size={24} /> Trade Returns
            </h1>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
              Manual Returns (TR-) • Session User: {currentUserId}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsHistoryOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-bold transition border border-slate-200"
            >
              <History size={15} /> History
            </button>
            {activeTab === "Pending" && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold transition shadow-sm"
              >
                <Plus size={16} /> New Trade Return
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[95%] mx-auto px-4 mt-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Trade Returns"
            value={tradeReturns.length}
            Icon={Layers}
            colorClass="bg-emerald-50 text-emerald-600"
          />
          <StatCard
            label="Pending Processing"
            value={pendingReturns.length}
            Icon={Clock}
            colorClass="bg-amber-50 text-amber-600"
          />
          <StatCard
            label="Total Cash Refunded"
            value={formatCurrency(totalRefunded)}
            Icon={TrendingDown}
            colorClass="bg-red-50 text-red-600"
          />
          <StatCard
            label="With Replacements"
            value={totalReplacements}
            Icon={RefreshCw}
            colorClass="bg-blue-50 text-blue-600"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 bg-slate-200/60 p-1.5 rounded-xl w-fit border border-slate-200">
          {[
            {
              id: "Pending",
              label: "Pending",
              icon: RotateCcw,
              badge: pendingReturns.length,
            },
            {
              id: "Processed",
              label: "Processed",
              icon: CheckCircle,
              badge: 0,
            },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  isActive
                    ? "bg-white text-emerald-700 shadow-sm ring-1 ring-black/5"
                    : "text-slate-600 hover:bg-white/50"
                }`}
              >
                <tab.icon
                  size={15}
                  className={isActive ? "text-emerald-600" : "text-slate-400"}
                />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Return # or Customer..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* ── PENDING TAB ── */}
            {activeTab === "Pending" && (
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-widest">
                  <tr>
                    <th className="px-5 py-3.5">Return #</th>
                    <th className="px-5 py-3.5">Customer</th>
                    <th className="px-5 py-3.5">Location</th>
                    <th className="px-5 py-3.5">Reason</th>
                    <th className="px-5 py-3.5">Created By</th>
                    <th className="px-5 py-3.5 text-right">Refund Value</th>
                    <th className="px-5 py-3.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginated.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-14 text-slate-400 text-sm"
                      >
                        No pending trade returns. Click{" "}
                        <strong>New Trade Return</strong> to start.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((ret) => {
                      // ✅ The Safety Gate: Disable if Pull Out but not Received
                      const isProcessLocked =
                        ret.logisticsType === "Pull Out" &&
                        ret.logisticsStatus !== "Received";

                      return (
                        <tr
                          key={ret.id}
                          className="hover:bg-slate-50/70 transition-colors"
                        >
                          <td className="px-5 py-4">
                            <div className="font-black text-slate-800 font-mono text-xs">
                              {ret.returnNumber}
                            </div>

                            {/* ✅ Render Logistics Tag */}
                            {ret.logisticsType === "Pull Out" ? (
                              <span
                                className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded mt-1 inline-block border ${
                                  ret.logisticsStatus === "Received"
                                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                    : ret.logisticsStatus === "Out for Pickup"
                                      ? "bg-blue-100 text-blue-700 border-blue-200"
                                      : "bg-amber-100 text-amber-700 border-amber-200"
                                }`}
                              >
                                {ret.logisticsStatus === "Received"
                                  ? "✓ Item Received"
                                  : `🚚 ${ret.logisticsStatus || "Pending Pickup"}`}
                              </span>
                            ) : (
                              <span className="text-[9px] font-black uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mt-1 inline-block border border-slate-200">
                                📦 Walk-in
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5 text-slate-700 font-semibold">
                              <User
                                size={12}
                                className="text-slate-400 shrink-0"
                              />{" "}
                              {ret.customerName}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-xs text-slate-500">
                              {ret.locationName || "—"}
                            </span>
                          </td>
                          <td className="px-5 py-4 max-w-[160px]">
                            <p className="text-xs text-slate-500 italic truncate">
                              {ret.reason || "—"}
                            </p>
                          </td>
                          <td className="px-5 py-4">
                            <div className="text-xs font-semibold text-slate-700">
                              {ret.createdByFullName || "—"}
                            </div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                              <Clock size={9} /> {formatDate(ret.createdDate)}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right font-black text-slate-700">
                            {formatCurrency(ret.totalRefundAmount)}
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openProcessModal(ret)}
                                disabled={isProcessLocked}
                                title={
                                  isProcessLocked
                                    ? "Waiting for driver to collect the item."
                                    : ""
                                }
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase transition shadow-sm ${
                                  isProcessLocked
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                                }`}
                              >
                                <Package size={13} /> Process Items
                              </button>
                              <button
                                onClick={() => handleDelete(ret)}
                                disabled={pendingDeleteId === ret.id}
                                className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                                title="Delete this return"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}

            {/* ── PROCESSED TAB ── */}
            {activeTab === "Processed" && (
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-widest">
                  <tr>
                    <th className="px-5 py-3.5 w-10"></th>
                    <th className="px-5 py-3.5">Return #</th>
                    <th className="px-5 py-3.5">Customer</th>
                    <th className="px-5 py-3.5">Location</th>
                    <th className="px-5 py-3.5 text-right">Cash Refund</th>
                    <th className="px-5 py-3.5">Processed By</th>
                    <th className="px-5 py-3.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginated.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-14 text-slate-400 text-sm"
                      >
                        No processed returns yet. Process items from the{" "}
                        <strong>Pending</strong> tab.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((ret) => (
                      <React.Fragment key={ret.id}>
                        <tr
                          onClick={() =>
                            setExpandedRow(
                              expandedRow === ret.id ? null : ret.id,
                            )
                          }
                          className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                        >
                          <td className="px-5 py-4 text-slate-400">
                            {expandedRow === ret.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-black text-slate-800 font-mono text-xs">
                              {ret.returnNumber}
                            </div>
                            <span className="text-[9px] font-black uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded mt-1 inline-flex items-center gap-0.5 border border-emerald-200">
                              <CheckCircle size={8} /> Processed
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                              <User size={12} className="text-slate-400" />{" "}
                              {ret.customerName}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-xs text-slate-500">
                              {ret.locationName || "—"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right font-black text-red-600 text-base">
                            {formatCurrency(ret.totalRefundAmount)}
                          </td>
                          <td className="px-5 py-4">
                            <div className="text-xs font-semibold text-slate-700">
                              {ret.processedByFullName || "—"}
                            </div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                              <Clock size={9} /> {formatDate(ret.processedDate)}
                            </div>
                          </td>
                          <td
                            className="px-5 py-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center">
                              <button
                                onClick={() => handleDelete(ret)}
                                disabled={pendingDeleteId === ret.id}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all border ${
                                  pendingDeleteId === ret.id
                                    ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                    : "bg-red-50 text-red-600 border-red-200 hover:bg-red-600 hover:text-white"
                                }`}
                              >
                                {pendingDeleteId === ret.id ? (
                                  <>
                                    <span className="animate-spin border-2 border-slate-400 border-t-transparent rounded-full w-3 h-3" />{" "}
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <Trash2 size={13} /> Delete
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded breakdown */}
                        {expandedRow === ret.id && (
                          <tr className="bg-slate-50/50">
                            <td colSpan={7} className="px-5 py-5 pl-16">
                              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm max-w-4xl">
                                <h4 className="text-[11px] font-black uppercase text-slate-400 flex items-center gap-2 mb-4">
                                  <Package size={14} /> Items Breakdown —{" "}
                                  {ret.returnNumber}
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs text-left">
                                    <thead className="bg-slate-100 text-[9px] uppercase text-slate-500 font-black">
                                      <tr>
                                        <th className="p-2.5 pl-4">Product</th>
                                        <th className="p-2.5 text-center">
                                          Condition
                                        </th>
                                        <th className="p-2.5 text-center">
                                          Qty Returned
                                        </th>
                                        <th className="p-2.5 text-center bg-red-50/50 text-red-500">
                                          Cash Return
                                        </th>
                                        <th className="p-2.5 text-center bg-blue-50/50 text-blue-500">
                                          Replace
                                        </th>
                                        <th className="p-2.5 text-right pr-4">
                                          Refund Value
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                      {(ret.items || []).map((item, idx) => (
                                        <tr
                                          key={idx}
                                          className="hover:bg-slate-50/50"
                                        >
                                          <td className="p-2.5 pl-4 font-bold text-slate-700">
                                            {item.productName}
                                          </td>
                                          <td className="p-2.5 text-center">
                                            <span
                                              className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                                item.condition === "Good"
                                                  ? "text-emerald-700 bg-emerald-50"
                                                  : "text-orange-700 bg-orange-50"
                                              }`}
                                            >
                                              {item.condition}
                                            </span>
                                          </td>
                                          <td className="p-2.5 text-center font-black text-slate-600">
                                            {item.quantityReturned}
                                          </td>
                                          <td className="p-2.5 text-center font-black text-red-600">
                                            {item.cashQty || 0}
                                          </td>
                                          <td className="p-2.5 text-center font-black text-blue-600">
                                            {item.replaceQty || 0}
                                          </td>
                                          <td className="p-2.5 text-right pr-4 font-black text-red-600">
                                            {formatCurrency(item.refundAmount)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {ret.note && (
                                  <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2 text-xs">
                                    <span className="font-black uppercase text-slate-400">
                                      Note:
                                    </span>
                                    <span className="text-slate-600 italic">
                                      {ret.note}
                                    </span>
                                  </div>
                                )}
                                {ret.reason && (
                                  <div className="mt-2 flex gap-2 text-xs">
                                    <span className="font-black uppercase text-slate-400">
                                      Reason:
                                    </span>
                                    <span className="text-slate-600 italic">
                                      {ret.reason}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && (
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={currentList.length}
            currentPage={currentPage}
            paginate={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}
